import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Tabs,
  Tab
} from "@mui/material";
import PostAddRoundedIcon from "@mui/icons-material/PostAddRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchIcon from "@mui/icons-material/Search";
import UserDataService from "../pages/services/UserService";
import LoanApplicationFeeService from "../pages/services/LoanApplicationFeeService";
import { fNumber, formatWithCommas, formatToTwoDecimals } from "../utils/formatNumber";

export default function LoanApplicationFeeModal({ open, onClose, onSuccess, initialMemberId }) {
  const [activeTab, setActiveTab] = useState(0); // 0: Record New Fee, 1: Application Fees Ledger
  const [members, setMembers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState(initialMemberId || "");
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Form fields
  const [loanAmountRequested, setLoanAmountRequested] = useState("");
  const tenorRequested = 6;
  const [formFeeAmount, setFormFeeAmount] = useState("1000"); // Standard default ₦1,000
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [receiptNo, setReceiptNo] = useState("");
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Ledger state
  const [feesList, setFeesList] = useState([]);
  const [summaryStats, setSummaryStats] = useState({ totalFormFeesCollected: 0, totalApplicants: 0, avgFormFee: 0 });
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Load members
  useEffect(() => {
    if (!open) return;
    if (initialMemberId) setSelectedMemberId(initialMemberId);
    setLoadingMembers(true);
    UserDataService.getAll()
      .then(res => {
        const list = res?.data || res || [];
        setMembers(Array.isArray(list) ? list : []);
      })
      .catch(() => {})
      .finally(() => setLoadingMembers(false));
  }, [open, initialMemberId]);

  // Load fees ledger
  const loadLedger = useCallback(async () => {
    setLedgerLoading(true);
    try {
      const res = await LoanApplicationFeeService.getAll({ search: searchQuery });
      const data = res?.data || {};
      setFeesList(data.records || []);
      setSummaryStats(data.summary || { totalFormFeesCollected: 0, totalApplicants: 0, avgFormFee: 0 });
    } catch (err) {
      console.error("loadLedger error:", err);
    } finally {
      setLedgerLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (open && activeTab === 1) {
      loadLedger();
    }
  }, [open, activeTab, loadLedger]);

  const handleSubmit = async () => {
    setError("");
    setSuccessMsg("");

    if (!selectedMemberId) {
      setError("Please select the applicant member.");
      return;
    }
    const numFee = Number(String(formFeeAmount).replace(/,/g, ""));
    if (!numFee || numFee <= 0) {
      setError("Please enter a valid application form fee amount.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        memberId: selectedMemberId,
        loanAmountRequested: Number(String(loanAmountRequested).replace(/,/g, "")) || 0,
        tenorRequested: Number(tenorRequested) || 6,
        formFeeAmount: numFee,
        paymentDate,
        paymentMethod,
        receiptNo: receiptNo.trim(),
        notes: notes.trim()
      };

      const res = await LoanApplicationFeeService.record(payload);
      setSuccessMsg(res?.data?.message || "Loan application form fee successfully recorded!");

      if (onSuccess) onSuccess(res?.data?.fee);
      setTimeout(() => {
        setActiveTab(1);
        loadLedger();
      }, 900);
    } catch (err) {
      console.error("recordFee error:", err);
      setError(err?.response?.data?.error || err.message || "Failed to record application form fee.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #0B3B60 0%, #1E3A8A 100%)",
          color: "#ffffff",
          p: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ReceiptLongRoundedIcon sx={{ fontSize: 30, color: "#FDE68A" }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Loan Application Form Fee Portal
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)" }}>
              Financial Secretary Desk — Record &amp; track pre-approval loan form fees paid by members
            </Typography>
          </Box>
        </Box>
        <Button onClick={onClose} sx={{ color: "#ffffff", minWidth: "auto" }}>
          <CloseRoundedIcon />
        </Button>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: "divider", backgroundColor: "#F8FAFC", px: 3 }}>
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)}>
          <Tab icon={<PostAddRoundedIcon />} iconPosition="start" label="Record Form Fee Payment" sx={{ fontWeight: 700, textTransform: "none" }} />
          <Tab icon={<ReceiptLongRoundedIcon />} iconPosition="start" label={`Application Fees Ledger (${summaryStats.totalApplicants || 0})`} sx={{ fontWeight: 700, textTransform: "none" }} />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {/* TAB 0: Record Form Fee */}
        {activeTab === 0 && (
          <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

            <Grid container spacing={2.5}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Applicant Member *"
                  value={selectedMemberId}
                  onChange={(e) => setSelectedMemberId(e.target.value)}
                  SelectProps={{ native: true }}
                  disabled={loadingMembers || submitting}
                  required
                >
                  <option value="">-- Choose Member --</option>
                  {members.map(m => (
                    <option key={m._id || m.id} value={m._id || m.id}>
                      {m.firstName ? `${m.firstName} ${m.lastName || ''}`.trim() : m.username} ({m.registrationNo || m.membershipId || m.username})
                    </option>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Payment Date *"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Application Form Fee Paid (₦) *"
                  value={formFeeAmount}
                  onChange={(e) => setFormFeeAmount(formatWithCommas(e.target.value))}
                  onBlur={() => {
                    if (formFeeAmount) setFormFeeAmount(formatToTwoDecimals(formFeeAmount));
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    inputMode: "numeric"
                  }}
                  helperText="Standard fee is ₦1,000 (can be adjusted)"
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Payment Method"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="bank_transfer">Bank Transfer / Direct Deposit</option>
                  <option value="cash">Cash Payment</option>
                  <option value="pos">POS Terminal</option>
                  <option value="deduction">Savings / Internal Deduction</option>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Proposed Loan Amount Requested (₦)"
                  placeholder="e.g. 200,000"
                  value={loanAmountRequested}
                  onChange={(e) => setLoanAmountRequested(formatWithCommas(e.target.value))}
                  onBlur={() => {
                    if (loanAmountRequested) setLoanAmountRequested(formatToTwoDecimals(loanAmountRequested));
                  }}
                  InputProps={{ startAdornment: <InputAdornment position="start">₦</InputAdornment> }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Receipt / Reference Number"
                  placeholder="e.g. REC-89410 or Leave blank for Auto-ID"
                  value={receiptNo}
                  onChange={(e) => setReceiptNo(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Secretary Notes (Optional)"
                  placeholder="e.g. Verified by Financial Secretary"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* TAB 1: Ledger Table */}
        {activeTab === 1 && (
          <Box>
            {/* KPI Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 2.5 }}>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #CBD5E1", backgroundColor: "#F0FDF4" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#166534" }}>
                    TOTAL FORM FEES COLLECTED
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#15803D" }}>
                    ₦{fNumber(summaryStats.totalFormFeesCollected || 0)}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #CBD5E1", backgroundColor: "#EFF6FF" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#1E40AF" }}>
                    TOTAL LOAN APPLICANTS
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#2563EB" }}>
                    {summaryStats.totalApplicants || 0}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, borderRadius: 2, border: "1px solid #CBD5E1", backgroundColor: "#FFFBEB" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#92400E" }}>
                    AVERAGE FORM FEE
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: "#D97706" }}>
                    ₦{fNumber(summaryStats.avgFormFee || 0)}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Search Box */}
            <TextField
              size="small"
              fullWidth
              placeholder="Search member name, receipt number, reg no..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "#94A3B8" }} />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />

            {ledgerLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                <CircularProgress size={32} />
              </Box>
            ) : feesList.length === 0 ? (
              <Typography align="center" sx={{ py: 4, color: "#64748B" }}>
                No loan application form fees recorded yet.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#F1F5F9" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Member</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Receipt #</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>Fee Paid (₦)</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800 }}>Loan Requested</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800 }}>Method</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {feesList.map(fee => (
                      <TableRow key={fee._id} hover>
                        <TableCell sx={{ fontSize: "0.85rem" }}>
                          {fee.paymentDate ? new Date(fee.paymentDate).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {fee.memberName}
                          {fee.memberRegNo && <Typography variant="caption" sx={{ display: "block", color: "#64748B" }}>#{fee.memberRegNo}</Typography>}
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: "0.8rem", color: "#0B3B60", fontWeight: 700 }}>
                          {fee.receiptNo || "—"}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 800, color: "#059669" }}>
                          ₦{fNumber(fee.formFeeAmount || 0)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: "0.85rem" }}>
                          ₦{fNumber(fee.loanAmountRequested || 0)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={fee.paymentMethod || "transfer"} size="small" sx={{ textTransform: "capitalize", fontSize: "0.72rem" }} />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label="PAID &amp; VERIFIED"
                            size="small"
                            sx={{ fontWeight: 800, fontSize: "0.7rem", backgroundColor: "#DCFCE7", color: "#15803D" }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, backgroundColor: "#F8FAFC", borderTop: "1px solid #E2E8F0" }}>
        <Button onClick={onClose} disabled={submitting} sx={{ fontWeight: 700 }}>
          Close
        </Button>
        {activeTab === 0 && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !selectedMemberId}
            startIcon={submitting ? <CircularProgress size={18} sx={{ color: "#ffffff" }} /> : <CheckCircleRoundedIcon />}
            sx={{
              backgroundColor: "#0B3B60",
              fontWeight: 800,
              textTransform: "none",
              px: 3,
              "&:hover": { backgroundColor: "#1E3A8A" }
            }}
          >
            {submitting ? "Recording..." : "Save Application Fee Record"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

LoanApplicationFeeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  initialMemberId: PropTypes.string
};
